import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus2, Inbox } from 'lucide-react';
import { AppCard, AppCardContent, EmptyState, UserAvatar, cn } from '../../../ui';
import CardHead from './CardHead';
import { timeAgo } from '../trainerDashboardUtils';

/**
 * One feed entry. Two shapes come from the server:
 *   assignment_submitted — a student handed something in (the one a trainer acts on)
 *   assignment_created   — an assignment was posted to one of their batches
 * The row is a link to the assignment when the entry names one.
 */
function ActivityItem({ item }) {
  const submitted = item.type === 'assignment_submitted';
  const at = item.at ? new Date(item.at) : null;
  const linked = !!item.assignmentId;
  const Row = linked ? Link : 'div';

  return (
    <li>
      <Row
        {...(linked ? { to: `/i/batch-assignments/${item.assignmentId}` } : {})}
        className={cn(
          'tw-flex tw-items-center tw-gap-3 tw-rounded-sm tw-px-2 tw-py-2 tw-text-content tw-no-underline',
          linked && 'tw-transition-colors tw-focus-ring hover:tw-bg-surface-hover',
        )}
      >
        {submitted ? (
          <UserAvatar name={item.studentName} size="sm" className="tw-shrink-0" />
        ) : (
          <span
            className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-primary-soft tw-text-primary-soft-fg"
            aria-hidden="true"
          >
            <FilePlus2 size={16} />
          </span>
        )}
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-block tw-truncate tw-text-[0.8125rem]">
            {submitted ? (
              <><span className="tw-font-semibold">{item.studentName}</span> submitted</>
            ) : (
              <span className="tw-font-semibold">New assignment</span>
            )}
          </span>
          <span className="tw-block tw-truncate tw-text-xs tw-text-content-muted">
            {item.title}
            {!submitted && item.batchName ? ` · ${item.batchName}` : ''}
          </span>
        </span>
        {at && !Number.isNaN(at.getTime()) && (
          <time dateTime={at.toISOString()} className="tw-shrink-0 tw-text-xs tw-text-content-subtle">
            {timeAgo(at)}
          </time>
        )}
      </Row>
    </li>
  );
}

/**
 * RecentActivityCard — the newest assignment events across the trainer's
 * batches: submissions to review and assignments posted.
 *
 * Props:
 *   items — the summary's `recentActivity`, newest first
 */
const RecentActivityCard = ({ items = [] }) => {
  const titleId = useId();
  return (
    <AppCard as="section" aria-labelledby={titleId}>
      <CardHead id={titleId} title="Recent activity" linkTo="/i/batch-assignments" linkLabel="Assignments" />
      <AppCardContent>
        {items.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Nothing yet"
            description="Submissions from your batches show up here."
          />
        ) : (
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-0.5 tw-p-0">
            {items.map((item, i) => (
              <ActivityItem key={`${item.type}-${item.assignmentId || 'x'}-${i}`} item={item} />
            ))}
          </ul>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default RecentActivityCard;
